import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useCommitImport, useCreateImport, useValidateImport } from '../../services/hooks';
import type { ImportJob } from '@grs-atlas/shared';
import { Toast } from '../../components/Toast';
import { config } from '../../config';

export function ImportPage() {
  const { mapId = '' } = useParams();
  const createImport = useCreateImport(mapId);
  const validateImport = useValidateImport(mapId);
  const commitImport = useCommitImport(mapId);
  const [job, setJob] = useState<ImportJob | null>(null);
  const [toast, setToast] = useState<{ message: string; type?: 'error' | 'success' } | null>(
    null,
  );
  const [busy, setBusy] = useState(false);

  const onFile = async (file: File) => {
    setBusy(true);
    try {
      const created = await createImport.mutateAsync({
        fileName: file.name,
        mode: 'upsert',
      });
      if (!created.uploadUrl) throw new Error('No upload URL returned');

      const put = await fetch(created.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'text/csv' },
        body: file,
      });
      if (!put.ok) throw new Error('Failed to upload CSV to storage');

      const validated = await validateImport.mutateAsync(created.importId);
      setJob(validated);
      setToast({ message: 'Preview ready. Review before committing.', type: 'success' });
    } catch (err) {
      setToast({
        message: err instanceof Error ? err.message : 'Import failed',
        type: 'error',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ padding: '1.5rem', maxWidth: 900, margin: '0 auto' }}>
      <p>
        <Link to={`/maps/${mapId}`}>← Back to map</Link>
      </p>
      <h1>Import CSV</h1>
      <p style={{ color: 'var(--muted)' }}>
        Upload a CSV, review the preview, then commit. Invalid rows are skipped unless you cancel.
      </p>
      <p>
        <a href={`${config.apiBaseUrl}/maps/${mapId}/exports/template`}>Download template</a>
      </p>

      <label className="panel" style={{ display: 'block', padding: '1rem' }}>
        Choose CSV file
        <input
          type="file"
          accept=".csv,text/csv"
          disabled={busy}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void onFile(file);
          }}
        />
      </label>

      {job?.preview && (
        <div style={{ marginTop: '1.5rem' }}>
          <h2>Preview</h2>
          <p>
            Inserts: {job.preview.inserts} · Updates: {job.preview.updates} · Unchanged:{' '}
            {job.preview.unchanged} · Errors: {job.preview.errors} · Warnings:{' '}
            {job.preview.warnings}
          </p>
          <div style={{ overflow: 'auto', maxHeight: 360 }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Row</th>
                  <th>Action</th>
                  <th>Name</th>
                  <th>Messages</th>
                </tr>
              </thead>
              <tbody>
                {job.preview.rows.map((r) => (
                  <tr key={r.rowNumber}>
                    <td>{r.rowNumber}</td>
                    <td>{r.action}</td>
                    <td>{r.name}</td>
                    <td>{r.messages.join('; ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {job.status === 'preview_ready' && (
            <div className="form-actions">
              <button
                type="button"
                className="btn"
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  try {
                    const committed = await commitImport.mutateAsync(job.importId);
                    setJob(committed);
                    setToast({ message: 'Import committed.', type: 'success' });
                  } catch (err) {
                    setToast({
                      message: err instanceof Error ? err.message : 'Commit failed',
                      type: 'error',
                    });
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                Commit valid rows
              </button>
            </div>
          )}
          {job.result && (
            <p>
              Result — inserted: {job.result.inserted}, updated: {job.result.updated}, skipped:{' '}
              {job.result.skipped}, failed: {job.result.failed}
            </p>
          )}
        </div>
      )}

      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
    </div>
  );
}
